import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Plus, Disc3, ToggleLeft, ToggleRight, Trash2, Pencil, X, Store, Clock,
} from "lucide-react";
import API from "../../api/axiosConfig";
import { toast } from "react-toastify";

const PRIZE_LABELS = {
  qpoints:            "QPUNTOS",
  guaranteed_qpoints: "QPUNTOS garantizado",
  coupon_percentage:  "Cupón % descuento",
  coupon_fixed:       "Cupón $ fijo",
  free_delivery:      "Domicilio gratis",
  spin_again:         "Gira otra vez",
};

const FREQ_PRESETS = [
  { hours: 1,   label: "Cada hora" },
  { hours: 6,   label: "Cada 6 horas" },
  { hours: 12,  label: "Cada 12 horas" },
  { hours: 24,  label: "Una vez al día" },
  { hours: 72,  label: "Cada 3 días" },
  { hours: 168, label: "Una vez a la semana" },
];

// ── Modal de segmento ───────────────────────────────────────────────────────
function SegmentModal({ campaignId, segment, companies, onClose, onSaved }) {
  const isEdit = !!segment;
  const [form, setForm] = useState({
    label:            segment?.label ?? "",
    color:            segment?.color ?? "#FEC400",
    weight:           segment?.weight ?? 1,
    prizeType:        segment?.prizeType ?? "qpoints",
    qpointsAmount:    segment?.qpointsAmount ?? 50,
    discountValue:    segment?.discountValue ?? 10,
    minOrderAmount:   segment?.minOrderAmount ?? 0,
    sponsorCompanyId: segment?.sponsorCompanyId ?? "",
    maxRedemptions:   segment?.maxRedemptions ?? "",
    displayOrder:     segment?.displayOrder ?? 0,
  });
  const [saving, setSaving] = useState(false);

  const needsQpoints = form.prizeType === "qpoints" || form.prizeType === "guaranteed_qpoints";
  const needsDiscount = form.prizeType === "coupon_percentage" || form.prizeType === "coupon_fixed";
  const canSponsor = needsDiscount || form.prizeType === "free_delivery";

  const save = async () => {
    if (!form.label.trim()) return toast.error("La etiqueta es obligatoria");
    setSaving(true);
    try {
      const payload = {
        label: form.label.trim(),
        color: form.color,
        weight: Number(form.weight) || 1,
        prizeType: form.prizeType,
        qpointsAmount: needsQpoints ? Number(form.qpointsAmount) || 0 : null,
        discountValue: needsDiscount ? Number(form.discountValue) || 0 : null,
        minOrderAmount: Number(form.minOrderAmount) || 0,
        sponsorCompanyId: canSponsor && form.sponsorCompanyId ? form.sponsorCompanyId : null,
        maxRedemptions: form.maxRedemptions !== "" ? Number(form.maxRedemptions) : null,
        displayOrder: Number(form.displayOrder) || 0,
      };
      if (isEdit) {
        await API.patch(`/wheel/admin/segments/${segment.id}`, payload);
        toast.success("Segmento actualizado");
      } else {
        await API.post(`/wheel/admin/campaigns/${campaignId}/segments`, payload);
        toast.success("Segmento creado");
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || "Error guardando el segmento");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h3 className="font-bold text-gray-900">{isEdit ? "Editar segmento" : "Nuevo segmento"}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700"><X size={18} /></button>
        </div>

        <div className="p-5 space-y-3">
          <div>
            <label className="text-xs font-semibold text-gray-600">Etiqueta</label>
            <input
              value={form.label}
              onChange={(e) => setForm(f => ({ ...f, label: e.target.value }))}
              placeholder="Ej: 20% en Donde Pepe"
              className="w-full mt-1 px-3 py-2 border border-gray-200 rounded-xl text-sm"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-gray-600">Color</label>
              <input
                type="color"
                value={form.color}
                onChange={(e) => setForm(f => ({ ...f, color: e.target.value }))}
                className="w-full mt-1 h-10 border border-gray-200 rounded-xl"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600">Peso (probabilidad relativa)</label>
              <input
                type="number" min="1" value={form.weight}
                onChange={(e) => setForm(f => ({ ...f, weight: e.target.value }))}
                className="w-full mt-1 px-3 py-2 border border-gray-200 rounded-xl text-sm"
              />
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold text-gray-600">Tipo de premio</label>
            <select
              value={form.prizeType}
              onChange={(e) => setForm(f => ({ ...f, prizeType: e.target.value }))}
              className="w-full mt-1 px-3 py-2 border border-gray-200 rounded-xl text-sm"
            >
              {Object.entries(PRIZE_LABELS).map(([k, label]) => (
                <option key={k} value={k}>{label}</option>
              ))}
            </select>
          </div>

          {needsQpoints && (
            <div>
              <label className="text-xs font-semibold text-gray-600">Cantidad de QPUNTOS</label>
              <input
                type="number" min="0" value={form.qpointsAmount}
                onChange={(e) => setForm(f => ({ ...f, qpointsAmount: e.target.value }))}
                className="w-full mt-1 px-3 py-2 border border-gray-200 rounded-xl text-sm"
              />
            </div>
          )}

          {needsDiscount && (
            <div>
              <label className="text-xs font-semibold text-gray-600">
                Valor del descuento {form.prizeType === "coupon_percentage" ? "(%)" : "($)"}
              </label>
              <input
                type="number" min="0" value={form.discountValue}
                onChange={(e) => setForm(f => ({ ...f, discountValue: e.target.value }))}
                className="w-full mt-1 px-3 py-2 border border-gray-200 rounded-xl text-sm"
              />
            </div>
          )}

          {canSponsor && (
            <div>
              <label className="text-xs font-semibold text-gray-600 flex items-center gap-1">
                <Store size={12} /> Tienda patrocinadora (opcional)
              </label>
              <select
                value={form.sponsorCompanyId}
                onChange={(e) => setForm(f => ({ ...f, sponsorCompanyId: e.target.value }))}
                className="w-full mt-1 px-3 py-2 border border-gray-200 rounded-xl text-sm"
              >
                <option value="">Premio de plataforma (sin patrocinador)</option>
                {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-gray-600">Cupo máximo</label>
              <input
                type="number" min="0" value={form.maxRedemptions}
                onChange={(e) => setForm(f => ({ ...f, maxRedemptions: e.target.value }))}
                placeholder="Sin límite"
                className="w-full mt-1 px-3 py-2 border border-gray-200 rounded-xl text-sm"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600">Orden</label>
              <input
                type="number" value={form.displayOrder}
                onChange={(e) => setForm(f => ({ ...f, displayOrder: e.target.value }))}
                className="w-full mt-1 px-3 py-2 border border-gray-200 rounded-xl text-sm"
              />
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 px-5 py-4 border-t border-gray-100">
          <button onClick={onClose} className="px-4 py-2 text-sm font-semibold text-gray-600 hover:bg-gray-50 rounded-xl">Cancelar</button>
          <button
            onClick={save} disabled={saving}
            className="px-4 py-2 text-sm font-semibold bg-supernova-500 text-black rounded-xl hover:bg-supernova-600 disabled:opacity-60"
          >
            {saving ? "Guardando..." : "Guardar"}
          </button>
        </div>
      </motion.div>
    </div>
  );
}

// ── Página principal ─────────────────────────────────────────────────────────
export default function WheelManager() {
  const [campaigns, setCampaigns] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState("");
  const [newInterval, setNewInterval] = useState(24);
  const [modalFor, setModalFor] = useState(null); // { campaignId, segment? }

  const load = async () => {
    try {
      const res = await API.get("/wheel/admin/campaigns");
      setCampaigns(res.data?.data?.campaigns ?? []);
    } catch (err) {
      toast.error(err.response?.data?.message || "Error cargando campañas");
    } finally {
      setLoading(false);
    }
  };

  const loadCompanies = async () => {
    try {
      const res = await API.get("/companies", { params: { limit: 500 } });
      const raw = res.data?.data?.data ?? [];
      setCompanies(Array.isArray(raw) ? raw : []);
    } catch { /* lista de patrocinadores es opcional */ }
  };

  useEffect(() => { load(); loadCompanies(); }, []);

  const createCampaign = async () => {
    if (!newName.trim()) return toast.error("Ponle un nombre a la campaña");
    try {
      await API.post("/wheel/admin/campaigns", { name: newName.trim(), freeSpinIntervalHours: Number(newInterval) });
      setNewName("");
      toast.success("Campaña creada");
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || "Error creando la campaña");
    }
  };

  const toggleActive = async (campaign) => {
    try {
      await API.patch(`/wheel/admin/campaigns/${campaign.id}`, { active: !campaign.active });
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || "Error actualizando la campaña");
    }
  };

  const updateInterval = async (campaign, hours) => {
    try {
      await API.patch(`/wheel/admin/campaigns/${campaign.id}`, { freeSpinIntervalHours: Number(hours) });
      toast.success("Frecuencia del giro gratis actualizada");
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || "Error actualizando la frecuencia");
    }
  };

  const toggleSegmentActive = async (segment) => {
    try {
      await API.patch(`/wheel/admin/segments/${segment.id}`, { active: !segment.active });
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || "Error actualizando el segmento");
    }
  };

  const deleteSegment = async (segment) => {
    if (!window.confirm(`¿Desactivar el segmento "${segment.label}"?`)) return;
    try {
      await API.delete(`/wheel/admin/segments/${segment.id}`);
      toast.success("Segmento desactivado");
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || "Error eliminando el segmento");
    }
  };

  if (loading) return <div className="p-6 text-gray-400">Cargando...</div>;

  return (
    <div className="max-w-4xl mx-auto px-4 py-6 pb-16">
      <div className="flex items-center gap-2 mb-6">
        <Disc3 size={22} className="text-supernova-500" />
        <h1 className="text-xl font-black text-gray-900">Ruleta de la fortuna</h1>
      </div>

      {/* Crear campaña */}
      <div className="flex flex-col sm:flex-row gap-2 mb-6">
        <input
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          placeholder="Nombre de la nueva campaña"
          className="flex-1 min-w-0 px-3 py-2.5 border border-gray-200 rounded-xl text-sm"
        />
        <select
          value={newInterval}
          onChange={(e) => setNewInterval(e.target.value)}
          className="px-3 py-2.5 border border-gray-200 rounded-xl text-sm"
          title="Frecuencia del giro gratis"
        >
          {FREQ_PRESETS.map((p) => <option key={p.hours} value={p.hours}>{p.label}</option>)}
        </select>
        <button
          onClick={createCampaign}
          className="flex items-center justify-center gap-1.5 px-4 py-2.5 bg-gray-900 text-white rounded-xl text-sm font-semibold hover:bg-gray-800 shrink-0"
        >
          <Plus size={15} /> Crear
        </button>
      </div>

      {campaigns.length === 0 ? (
        <p className="text-center text-gray-400 py-16">Todavía no hay ninguna campaña de ruleta.</p>
      ) : (
        <div className="space-y-6">
          {campaigns.map((c) => (
            <div key={c.id} className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-5 py-4 border-b border-gray-100">
                <div>
                  <p className="font-bold text-gray-900">{c.name}</p>
                  <p className="text-xs text-gray-400">{c.segments.length} segmento(s)</p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <label className="flex items-center gap-1.5 text-xs text-gray-500">
                    <Clock size={13} className="shrink-0" />
                    <select
                      value={c.freeSpinIntervalHours ?? 24}
                      onChange={(e) => updateInterval(c, e.target.value)}
                      className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs max-w-[9.5rem]"
                    >
                      {FREQ_PRESETS.map((p) => <option key={p.hours} value={p.hours}>{p.label}</option>)}
                    </select>
                  </label>
                  <button
                    onClick={() => setModalFor({ campaignId: c.id })}
                    className="flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 shrink-0"
                  >
                    <Plus size={13} /> Segmento
                  </button>
                  <button
                    onClick={() => toggleActive(c)}
                    className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg border shrink-0 ${
                      c.active ? "border-emerald-200 text-emerald-700 bg-emerald-50" : "border-gray-200 text-gray-500"
                    }`}
                  >
                    {c.active ? <><ToggleRight size={14} /> Activa</> : <><ToggleLeft size={14} /> Inactiva</>}
                  </button>
                </div>
              </div>

              <div className="divide-y divide-gray-100">
                <AnimatePresence>
                  {c.segments.map((s) => (
                    <motion.div
                      key={s.id}
                      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                      className={`flex items-center gap-3 px-5 py-3 ${!s.active ? "opacity-50" : ""}`}
                    >
                      <div className="w-3 h-3 rounded-full shrink-0" style={{ background: s.color || "#ccc" }} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-800 truncate">{s.label}</p>
                        <p className="text-[11px] text-gray-400">
                          {PRIZE_LABELS[s.prizeType] ?? s.prizeType} · peso {s.weight}
                          {s.maxRedemptions !== null && ` · ${s.redeemedCount}/${s.maxRedemptions} entregados`}
                        </p>
                      </div>
                      <button onClick={() => setModalFor({ campaignId: c.id, segment: s })} className="text-gray-400 hover:text-gray-700">
                        <Pencil size={14} />
                      </button>
                      <button onClick={() => toggleSegmentActive(s)} className="text-gray-400 hover:text-gray-700">
                        {s.active ? <ToggleRight size={16} className="text-emerald-600" /> : <ToggleLeft size={16} />}
                      </button>
                      <button onClick={() => deleteSegment(s)} className="text-gray-400 hover:text-red-500">
                        <Trash2 size={14} />
                      </button>
                    </motion.div>
                  ))}
                </AnimatePresence>
                {c.segments.length === 0 && (
                  <p className="text-center text-xs text-gray-400 py-6">Sin segmentos todavía — agrega el primero.</p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {modalFor && (
        <SegmentModal
          campaignId={modalFor.campaignId}
          segment={modalFor.segment}
          companies={companies}
          onClose={() => setModalFor(null)}
          onSaved={load}
        />
      )}
    </div>
  );
}
